import type {SessionsNavItemProps} from './types'
import {getTaskTitle} from '../../../utils/task-helpers'
import {useResolvedTaskTitle} from '@github-ui/copilot-shared-ui/hooks/use-task-title-override'
import {getReadableSessionState, normalizeSessionStatusLabel} from '../../../types/session'
import {getEffectiveSource, SOURCE_LABELS} from '../../../types/origin'
import {resolveAgentProfileForTask} from '../../../agents/resolve-agent-profile'
import {DefaultTrailingVisual, EnvironmentTrailingVisual} from '../../../agents/profiles/DefaultTrailingVisual'
import {isQuickChatTask} from '../../../utils/is-quick-chat-task'
import styles from './SessionsNavItemHovercard.module.css'
import hovercardStyles from '@github-ui/copilot-shared-ui/components/SidebarItemHovercard.module.css'
import {Label, RelativeTime} from '@primer/react'
import {CloudIcon, RepoIcon, GitBranchIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {
  useAnnounceSidebarEnvironmentStatus,
  useSidebarTaskEnvironmentStatus,
} from '../../../hooks/use-sidebar-task-environment-status'
import {useEnvironmentFromApi} from '../../../hooks/use-environment-from-api'
import {ENVIRONMENT_KIND_LABELS} from '@github-ui/copilot-shared-ui/environments/environment-display'
import {resolveEffectiveBranchName} from '../../../utils/task-artifact-helpers'

const DONE_STATUS_LABEL = 'Done'
const FAILED_STATUS_LABEL = 'Failed'

export const SessionsNavItemHovercard = ({
  task,
  preferPullTitle,
  formatAsReview,
  statusLabel,
  environmentStatusApiURL,
}: Pick<
  SessionsNavItemProps,
  'task' | 'preferPullTitle' | 'formatAsReview' | 'statusLabel' | 'environmentStatusApiURL'
>) => {
  const resolvedStatusLabel = normalizeSessionStatusLabel(
    statusLabel ?? getReadableSessionState(task.latest_session_state, {hasPull: task.pull != null}),
  )
  const environmentKind = task.current_environment?.kind
  const isUserEnvironment = environmentKind === 'user-local' || environmentKind === 'user-codespace'
  const {data: environment} = useEnvironmentFromApi(task.current_environment?.id, environmentStatusApiURL, {
    enabled: isUserEnvironment,
  })
  const sourceLabel = environmentKind
    ? environmentKind === 'managed-sandbox'
      ? 'Cloud'
      : isUserEnvironment
        ? environment?.name || ENVIRONMENT_KIND_LABELS[environmentKind]
        : 'Environment'
    : SOURCE_LABELS[getEffectiveSource(task.origin, task)]
  const branch = resolveEffectiveBranchName(undefined, task, task.pull)
  const environmentStatus = useSidebarTaskEnvironmentStatus(task, {apiURL: environmentStatusApiURL})
  useAnnounceSidebarEnvironmentStatus(environmentStatus, true, environmentStatusApiURL)
  const title = useResolvedTaskTitle(task.id, getTaskTitle(task, task.pull?.title, {preferPullTitle, formatAsReview}))
  // Keep environment details in the hovercard while the row prioritizes branch status.
  const TrailingVisual = resolveAgentProfileForTask(task).slots.TrailingVisual
  const ChatEnvironmentVisual = environmentKind
    ? environmentKind === 'managed-sandbox'
      ? DefaultTrailingVisual
      : EnvironmentTrailingVisual
    : null

  return (
    <>
      <div className={hovercardStyles.header}>
        <Label
          variant={
            resolvedStatusLabel === DONE_STATUS_LABEL
              ? 'success'
              : resolvedStatusLabel === FAILED_STATUS_LABEL
                ? 'danger'
                : 'secondary'
          }
          className={styles.status}
        >
          {resolvedStatusLabel}
        </Label>
        <RelativeTime
          format="micro"
          datetime={task.latest_session_updated_at}
          className={clsx(hovercardStyles.timestamp, styles.timestamp)}
        />
      </div>
      <div className={hovercardStyles.title}>{title}</div>
      <ul className={hovercardStyles.metaList}>
        <li className={clsx(hovercardStyles.metaRow, styles.metaRow)}>
          <span className={clsx(hovercardStyles.metaIcon, styles.metaIcon)}>
            {isQuickChatTask(task) && ChatEnvironmentVisual ? (
              <ChatEnvironmentVisual />
            ) : TrailingVisual ? (
              <TrailingVisual
                state={task.latest_session_state}
                staleAt={task.stale_at}
                sharingStatus={task.sharing_status}
              />
            ) : (
              <CloudIcon size={16} />
            )}
          </span>
          <span className={clsx(hovercardStyles.metaValue, styles.environmentName)}>{sourceLabel}</span>
          {environmentStatus && (
            <span className={styles.environmentStatus} data-testid="environment-status-container">
              <Label variant={environmentStatus.variant} data-testid="environment-status">
                {environmentStatus.label}
              </Label>
              {environmentStatus.refreshFailed ? (
                <span className={styles.environmentStatusMessage}>· Update failed</span>
              ) : null}
            </span>
          )}
        </li>
        {task.nwo != null && (
          <li className={clsx(hovercardStyles.metaRow, styles.metaRow)}>
            <RepoIcon size={16} className={clsx(hovercardStyles.metaIcon, styles.metaIcon)} />
            <span className={hovercardStyles.metaValue}>{task.nwo}</span>
          </li>
        )}
        {branch && (
          <li className={clsx(hovercardStyles.metaRow, styles.metaRow)}>
            <GitBranchIcon size={16} className={clsx(hovercardStyles.metaIcon, styles.metaIcon)} />
            <span className={hovercardStyles.metaValue}>{branch}</span>
          </li>
        )}
      </ul>
    </>
  )
}
